import { Ref, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
    useMap,
} from '@vis.gl/react-google-maps';
import { deepCopyObject, isArrayEmpty, isNullOrEmpty, isObjectNull } from '../../../helpers/ObjectHelpers';
import { sleep } from '../../../helpers/DataHelpers';
import MapService, { IMapService } from '../services/MapService';
import MapApiRepository  from '../repositories/MapApiRepository';
import MapPropertiesService from '../services/MapPropertiesService';
import { MapPolygon } from '../components/MapPolygon';
import { MapPolyline } from './MapPolyline';
import { MapCircle } from './MapCircle';
import {
    useSetShowLoader,
    useActivateMultiSelect,
    useMapDragStart,
    useMapDragEnd,
    useMapBounds,
    useMapZoom,
    useMapResize,
    useDataClicked,
    useMapClicked,
    useSetMapTitle
} from '../hooks/useMapStore';

interface MapPcsGroupRef {
    onDragStart: () => void,
    onChange: () => void,
    onMultiSelectOk: () => void,
    onMultiSelectCancel: () => void,
}

interface MapPcsGroupProp {
    ref: Ref<MapPcsGroupRef>,
    pcsGroupId: number,
    onFeatureClick: (e: any) => void,
    onInitFeatures?: (e: number[]) => void,

}

export const MapPcsGroup = (
    {
        ref,
        pcsGroupId,
        onFeatureClick,
        onInitFeatures,
    }: MapPcsGroupProp) => {

    useImperativeHandle(ref, () => ({
        onDragStart() {
            stopLoaderTimer();
            setShowLoader(false);
            localRef.hasCancelled = true;
        },
        onChange() {
            localRef.hasCancelled = true;
            if (localRef.isBusy) return;
            loadSegmentsAsync();
        },
        onMultiSelectOk() {
            searchMultiSelectAsync();
        },
        onMultiSelectCancel() {
            cancelMultiSelectSearch();
        },
    }));

    const map = useMap();

    const setShowLoader = useSetShowLoader();
    const activateMultiSelect = useActivateMultiSelect();
    const dragStart = useMapDragStart();
    const dragEnd = useMapDragEnd();
    const bounds = useMapBounds();
    const zoom = useMapZoom();
    const resize = useMapResize();
    const dataClicked = useDataClicked();
    const mapClicked = useMapClicked();
    const setMapTitle = useSetMapTitle();
    
    const mapService: IMapService = new MapService();
    const mapProperties = MapPropertiesService.properties;
    const maxCount = 100;

    const componentRef: {
        current: {
            hasCancelled: boolean,
            sourceSegmentIds: number[],
            selectedSegmentIds: number[],
            selectedCoordinates: any[],
            segmentIds: number[],
            hasInitialized: boolean,
            isBusy: boolean,
            selectedPolyline: any,
            selectedPolygon: any,
            timer: any,
            changeTimer: any,
            loaderTimer: any
        }
    } = useRef({
        hasCancelled: false,
        sourceSegmentIds: [],
        selectedSegmentIds: [],
        selectedCoordinates: [],
        segmentIds: [],
        hasInitialized: false,
        isBusy: false,
        selectedPolyline: null,
        selectedPolygon: null,
        timer: null,
        changeTimer: null,
        loaderTimer: null

    });
    const { current: localRef } = componentRef;

    const polygonRefs: {current: any[] } = useRef([]);

    const [features, setFeatures] = useState<any[]>([]);
    const [newFeatures, setNewFeatures] = useState<any[]>([]);
    const [selectedPaths, setSelectedPaths] = useState<any[]>([]);
        
    useEffect(() => {
        stopLoaderTimer();
        setShowLoader(false);
        localRef.hasCancelled = true;
    }, [dragStart]);

    
    useEffect(() => {
        handleMapOnClick(dataClicked)
    }, [dataClicked])

    useEffect(() => {
        localRef.hasCancelled = true;
        if (localRef.isBusy) return;
        loadSegmentsAsync();    
    }, [bounds, zoom, dragEnd, resize]);
        
    useEffect(() => {
        if (!mapClicked) return;
        const latLng = new window.google.maps.LatLng(mapClicked.latLng.lat, mapClicked.latLng.lng);
        handleMapOnClick(latLng);
    }, [mapClicked]);

    useEffect(() => {
        setMapTitle("");
        return (() => {
            mapService.removeFeatures(map, "featureType", mapProperties.PcsFeatureType);
            setFeatures([]);
            setNewFeatures([]);
        });
    }, []);

    useEffect(() => {
        if (!map) return;
        initializeAsync();
    }, [map]);

    useEffect(() => {
        if (!map) return;

        if (!isArrayEmpty(newFeatures)) {
            const filteredFeatures: any = [];
            for (const feature of newFeatures) {
                const segmentId = feature.properties.segmentId;
                if (localRef.segmentIds.includes(segmentId)) continue;

                localRef.segmentIds.push(segmentId);
                filteredFeatures.push(feature);
            }
            setFeatures([...features, ...filteredFeatures]);
        }

        startLoaderTimer();
    }, [newFeatures]);

    return (
        <>
            {
                features.map((feature: any, index: number) =>
                    <MapPolygon
                        ref={(e:any) => (polygonRefs.current[index] = e)}
                        key={index}
                        source={feature}
                        disabled={activateMultiSelect}
                        onClick={handleOnClick}
                    />
                )
            }
            {
                (!isArrayEmpty(selectedPaths) && activateMultiSelect) &&
                <>
                    {
                        selectedPaths.map((path: any, index: number) =>
                            <MapCircle key={index} center={path} />
                        )
                    }
                    {
                        (selectedPaths.length > 1) &&
                            <MapPolyline paths={selectedPaths} />
                    }
                    {
                        (selectedPaths.length > 2) &&
                            <MapPolygon
                                ref={null}
                                disabled={true}
                                source={{
                                    paths: selectedPaths,
                                    properties: { featureType: mapProperties.MultiFeatureType }
                                }}
                                onClick={handleOnClick}
                            />
                    }
                </>
            }
        </>
        
    );

    async function initializeAsync() {

        if (!map) return;

        localRef.isBusy = true;
        setShowLoader(true);

        if (pcsGroupId > 0) {
            const response: any = await MapApiRepository.getPcsGroupAsync(pcsGroupId);
            if (response && response.ok) {
                const data = await response.json();

                localRef.sourceSegmentIds = deepCopyObject(data.coastalSegmentIds);
                localRef.selectedSegmentIds = deepCopyObject(data.coastalSegmentIds);

                let count = 0;
                let features = [];

                const bounds = new google.maps.LatLngBounds();
                const segmentIds: number[] = [];

                for (const feature of data.geoJson.features) {

                    prepareFeature(feature);
                    mapService.addFeaturePathBounds(bounds, feature);
                    features.push(feature);
                    segmentIds.push(feature.properties.segmentId);

                    count++;
                    if (count % maxCount === 0) {
                        setNewFeatures(features);
                        features = [];
                        await sleep(50);
                    }
                }

                if (!isArrayEmpty(features)) {
                    setNewFeatures(features);
                }

                map.fitBounds(bounds);
                if (onInitFeatures)
                    onInitFeatures(segmentIds);
            }
        } else {
            mapService.updateZoomByUrlParameter(map);
        }

        setShowLoader(false);

        loadSegmentsAsync();

        localRef.isBusy = false;
    }

    function handleMapOnClick(e:any) {
        if (localRef.isBusy || !activateMultiSelect) return;
        onMultiClick(e);
    }

    function handleOnClick(feature: any, latLng?: any) {
        if (localRef.isBusy) return;
        
        if (!activateMultiSelect) {
            handleOnFeatureClick(feature);
        } else {
            onMultiClick(latLng);
        }
    }

    function handleOnFeatureClick(feature: any) {
        const featureType = feature.properties.featureType;;
        if (featureType !== mapProperties.PcsFeatureType) return;

        const isSelected = feature.properties.isSelected;
        const segmentId = feature.properties.segmentId;

        if (isSelected) {
            if (!isInSelectedSegmentIds(segmentId)) {
                localRef.selectedSegmentIds.push(segmentId);
            }
        } else {
            localRef.selectedSegmentIds = localRef.selectedSegmentIds.filter(i => i !== segmentId);
        }
        
        onFeatureClick({
            segmentIds: deepCopyObject(localRef.selectedSegmentIds)
        });
    }

    function onMultiClick(latLng: google.maps.LatLng) {
        localRef.selectedCoordinates.push({ lat: latLng.lat(), lng: latLng.lng() });
        if (localRef.selectedCoordinates.length === 0) return;

        const copySelectedPaths = deepCopyObject(localRef.selectedCoordinates);
        if (localRef.selectedCoordinates.length > 2) {
            copySelectedPaths.push(localRef.selectedCoordinates[0]);
        }

        setSelectedPaths(copySelectedPaths);
    }

    async function loadSegmentsAsync() {
        if (!map) return;
        const zoom = map.getZoom();
        if (!zoom) return;

        if (zoom < mapProperties.FeaturesVisibleAtZoom) return;

        try {
            setShowLoader(true);
            const searchParameter = createSearchParameter();
            const response: any = await MapApiRepository.searchPcsAsync(searchParameter);
            if (response.ok) {
                const data = await response.json();
                await addFeatures(data.features);
            }
        } catch (error:any) {
            if (error.name === 'AbortError') {
                console.log('Request was aborted');
            } else {
                console.error('Request failed', error);
            }
        } finally {
            isBusy(false);
        }
    }

    async function addFeatures(features: any) {

        if (!map) return;

        localRef.hasCancelled = false;

        let count = 0;
        let newFeatures: any[] = [];

        for (const feature of features) {
            if (localRef.hasCancelled) break;

            if (hasSegmentId(feature)) continue;

            prepareFeature(feature);
            setShowLoader(true);

            newFeatures.push(feature);

            count++;
            if (count % maxCount === 0) {
                setNewFeatures(newFeatures);
                newFeatures = [];
                await sleep(50);
            }
        }

        if (localRef.hasCancelled) return;
        setNewFeatures(newFeatures);
    }

    function prepareFeature(feature: any) {
        feature.properties.isVisible = true;
        feature.properties.featureType = mapProperties.PcsFeatureType;
        
        if (feature.geometry) {
            feature.paths = mapService.getGeometryPaths(feature.geometry);
        } else {
            mapService.setFeatureGeometryAndBounds(feature);
        }
    }

    async function searchMultiSelectAsync() {
        if (!map) return;

        isBusy(true);
        setShowLoader(true);
        
        if (localRef.selectedCoordinates.length > 2) {

            const response: any = await MapApiRepository.searchPcsByAreaAsync(localRef.selectedCoordinates);
            if (response.ok) {

                const data = await response.json();
                const searchedSegmentIds:number[] = [];

                for (const feature of data.features) {
                    const segmentId = feature.properties.segmentId;
                    const segmentGroupId = feature.properties.segmentGroupId;
                    if (segmentGroupId > 0) continue;

                    searchedSegmentIds.push(segmentId);
                    if (!isInSelectedSegmentIds(segmentId)) {
                        localRef.selectedSegmentIds.push(segmentId);
                    }
                }

                if (!isArrayEmpty(searchedSegmentIds)) {
                    for (const ref of polygonRefs.current) {
                        if (isNullOrEmpty(ref)) continue;
                        ref.onSelect(searchedSegmentIds);
                    }
                }
            }
        }

        localRef.selectedCoordinates = [];
        setSelectedPaths([]);
        
        onFeatureClick({
            segmentIds: deepCopyObject(localRef.selectedSegmentIds)
        });

        isBusy(false);
        setShowLoader(false);
    }

    function cancelMultiSelectSearch() {
        if (!map) return;

        setSelectedPaths([]);
        localRef.selectedCoordinates = [];
        isBusy(false);
    }

    function createSearchParameter() {
        const rectangle = mapService.getBoundsRectangle(map);

        return {
            Rectangle: rectangle,
            SegmentIds: localRef.segmentIds,
            GroupId: pcsGroupId
        }
    }

    function hasSegmentId(feature:any) {
        return localRef.segmentIds.includes(feature.properties.segmentId);
    }

    function isBusy(busy: boolean) {
        localRef.isBusy = busy;
    }

    function isInSelectedSegmentIds(segmentId:number) {
        return !isObjectNull(localRef.selectedSegmentIds.find(i => i === segmentId));
    }

    function startLoaderTimer() {
        stopLoaderTimer();
        localRef.loaderTimer = setTimeout(() => {
            setShowLoader(false);
        }, 250);
    }

    function stopLoaderTimer() {
        clearTimeout(localRef.loaderTimer);
    }   
}
